import React, { FC, useCallback, useEffect, useMemo } from 'react';
import styles from './index.module.scss';
import { useTasks } from 'src/models/bids/BidFull/hooks';
import { useTranslation } from 'react-i18next';
import Task from '../Task';
import { BidQuestionType } from 'src/models/bids/BidFull/types';
import { Button, Paging, PagingProps, Skeleton, Spinner } from 'src/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { useCreateBidTask } from 'src/models/bids/BidTask/hooks';

function calcPage(offset: number, limit: number): number {
  return Math.trunc(offset / limit) + 1;
}

interface TasksListProps {
  tasksType: BidQuestionType;
}

export const TasksList: FC<TasksListProps> = ({ tasksType }) => {
  const { t } = useTranslation();
  const { manual, generated, selectedTasks, setSelectedTaskIds } = useTasks();
  const {
    data: state,
    offset,
    setOffset,
    type,
    groupId,
    limit
  } = tasksType === BidQuestionType.Manual ? manual : generated;
  const tasks = useMemo(() => (state?.data ? state.data.tasks : []), [state?.data]);
  const currentTasks = useMemo(() => tasks.slice(offset, offset + limit), [limit, offset, tasks]);
  const lastOffset = useMemo(() => Math.trunc((state?.data?.totalCount ?? 0) / limit) * limit, [state?.data, limit]);
  const currentPage = useMemo(() => calcPage(offset, limit), [offset, limit]);

  const [createTask] = useCreateBidTask();

  const onChangeSelection = useCallback(
    (taskId: string, checked: boolean) => {
      const selectedTaskIds = selectedTasks.map(item => item.id);
      setSelectedTaskIds(checked ? [...selectedTaskIds, taskId] : selectedTaskIds.filter(item => item !== taskId));
    },
    [selectedTasks, setSelectedTaskIds]
  );

  const onCreateTask = useCallback(() => {
    if (tasks.slice(lastOffset, lastOffset + limit).some(task => !task)) {
      state?.fetchMore?.({ variables: { offset: lastOffset } }).then(() => {
        lastOffset !== offset && setOffset(lastOffset);
        createTask({ groupId });
      });
    } else {
      createTask({
        groupId,
        onComplete() {
          lastOffset !== offset && setOffset(lastOffset);
        }
      });
    }
  }, [tasks, lastOffset, limit, state, offset, setOffset, createTask, groupId]);

  const onChange: PagingProps['onChange'] = useCallback(
    page => {
      const currentOffset = (page - 1) * limit;
      if (tasks.slice(currentOffset, currentOffset + limit).some(task => !task)) {
        state?.fetchMore?.({ variables: { offset: currentOffset } }).then(() => {
          setOffset(currentOffset);
        });
      } else {
        setOffset(currentOffset);
      }
    },
    [limit, setOffset, state, tasks]
  );

  useEffect(() => {
    if (offset === state?.data?.totalCount && offset !== 0) {
      const prevOffset = offset - limit;
      setOffset(prevOffset);
    }
  }, [limit, offset, setOffset, state?.data?.totalCount]);

  return state ? (
    <div className={styles.tasks}>
      {state.loading ? (
        <Skeleton active loading />
      ) : (
        !!tasks.length && (
          <>
            <h3 className={styles.title}>
              {t(
                type === BidQuestionType.Generated ? 'BiddingTool.generatedTasksTitle' : 'BiddingTool.manualTasksTitle'
              )}
            </h3>
            {currentTasks.map(
              task =>
                task && (
                  <Task
                    key={task.id}
                    task={task}
                    type={tasksType}
                    selectedTasks={selectedTasks}
                    onChangeSelection={onChangeSelection}
                  />
                )
            )}
            <Paging
              defaultCurrent={currentPage}
              current={currentPage}
              onChange={onChange}
              pageSize={limit}
              total={state.data?.totalCount ?? 0}
              className={styles.paging}
              itemRender={
                state.fetchingMore
                  ? (page, type, originalElement) =>
                      page === currentPage && type === 'page' ? <Spinner /> : originalElement
                  : undefined
              }
            />
          </>
        )
      )}
      {type === BidQuestionType.Manual && (
        <Button type={'link'} className={styles.button} onClick={onCreateTask} icon={<FontAwesomeIcon icon={faPlus} />}>
          {t('BiddingTool.addTask')}
        </Button>
      )}
    </div>
  ) : null;
};

export default TasksList;
